import React, { useState, useEffect, useRef } from 'react';
import { useTheme, createThemedStyles } from '../../contexts/ThemeContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faCheck } from '@/icons';
import type { BaseCalculatorProps } from './types';

export const MonthlyCalculator: React.FC<BaseCalculatorProps> = ({
    currentValue,
    unit,
    decimalPlaces,
    onSave,
    onClose,
}) => {
    const theme = useTheme();
    const themedStyles = createThemedStyles(theme);
    const yearlyAmountRef = useRef<HTMLInputElement>(null);
    const [yearlyAmount, setYearlyAmount] = useState('');
    const [monthlyAmount, setMonthlyAmount] = useState(0);

    useEffect(() => {
        if (currentValue && !isNaN(parseFloat(currentValue)) && parseFloat(currentValue) > 0) {
            const value = parseFloat(currentValue);
            setYearlyAmount(unit === 'month' ? (value * 12).toString() : value.toString());
        } else {
            setYearlyAmount('');
        }
    }, [currentValue, unit]);

    useEffect(() => {
        yearlyAmountRef.current?.focus();
    }, []);

    useEffect(() => {
        const yearly = parseFloat(yearlyAmount) || 0;
        setMonthlyAmount(yearly / 12);
    }, [yearlyAmount]);

    const handleSave = () => {
        const rounded = Math.round(monthlyAmount * Math.pow(10, decimalPlaces)) / Math.pow(10, decimalPlaces);
        onSave(rounded);
        onClose();
    };

    return (
        <>
            <div style={{
                backgroundColor: '#dbeafe',
                border: '1px solid #93c5fd',
                borderRadius: theme.borderRadius.sm,
                padding: theme.spacing.md,
                marginBottom: theme.spacing.lg,
                display: 'flex',
                alignItems: 'flex-start',
                gap: theme.spacing.sm,
            }}>
                <i className="fas fa-info-circle" style={{ color: '#1e40af', fontSize: theme.typography.fontSize.md, marginTop: '2px' }} />
                <p style={{ margin: 0, fontSize: theme.typography.fontSize.sm, color: '#1e40af', lineHeight: 1.5 }}>
                    Enter the yearly amount and Calculist will calculate the monthly equivalent for you.
                </p>
            </div>
            <div style={{ marginBottom: theme.spacing.lg }}>
                <label style={{ display: 'block', fontSize: theme.typography.fontSize.sm, fontWeight: theme.typography.fontWeight.bold, color: theme.colors.text.primary, marginBottom: theme.spacing.sm }}>Yearly Amount:</label>
                <input
                    ref={yearlyAmountRef}
                    type="number"
                    value={yearlyAmount}
                    onChange={(e) => setYearlyAmount(e.target.value)}
                    style={{ width: '100%', padding: theme.spacing.sm, border: `1px solid ${theme.colors.border.default}`, borderRadius: theme.borderRadius.sm, fontSize: theme.typography.fontSize.sm }}
                    placeholder="0.00"
                    step="0.01"
                />
            </div>
            <div style={{ marginBottom: theme.spacing.xl }}>
                <label style={{ display: 'block', fontSize: theme.typography.fontSize.sm, fontWeight: theme.typography.fontWeight.bold, color: theme.colors.text.primary, marginBottom: theme.spacing.sm }}>Monthly Amount:</label>
                <div style={{ padding: theme.spacing.sm, backgroundColor: theme.colors.surfaceSecondary, border: `1px solid ${theme.colors.border.default}`, borderRadius: theme.borderRadius.sm, fontSize: theme.typography.fontSize.md, fontWeight: theme.typography.fontWeight.medium, color: theme.colors.text.primary }}>
                    ${monthlyAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: theme.spacing.sm }}>
                <button onClick={onClose} style={{ ...themedStyles.button.primary, backgroundColor: 'transparent', color: theme.colors.text.primary, border: `1px solid ${theme.colors.border.default}`, padding: `${theme.spacing.sm} ${theme.spacing.lg}`, display: 'flex', alignItems: 'center', gap: theme.spacing.sm }}>
                    <FontAwesomeIcon icon={faTimes} /> Cancel
                </button>
                <button onClick={handleSave} style={{ ...themedStyles.button.primary, backgroundColor: theme.colors.primary, color: theme.colors.text.inverse, padding: `${theme.spacing.sm} ${theme.spacing.lg}`, display: 'flex', alignItems: 'center', gap: theme.spacing.sm }}>
                    <FontAwesomeIcon icon={faCheck} /> Apply
                </button>
            </div>
        </>
    );
};
